import { Capacitor } from '@capacitor/core';

/**
 * iOS Haptic Engine Integration
 * 
 * Provides haptic feedback following iOS Human Interface Guidelines:
 * - Selection feedback for tab changes
 * - Impact feedback for button presses
 * - Notification feedback for alerts/confirmations
 * - Error feedback for validation failures
 */

export interface HapticFeedbackOptions {
  intensity?: 'light' | 'medium' | 'heavy';
  duration?: number;
}

export interface HapticNotificationOptions {
  type: 'success' | 'warning' | 'error';
}

class HapticEngineManager {
  private isAvailable: boolean = false;
  private isIOS: boolean = false;

  constructor() {
    this.initialize();
  }

  private async initialize(): Promise<void> {
    try {
      this.isIOS = Capacitor.getPlatform() === 'ios';
      this.isAvailable = this.isIOS && 'vibrate' in navigator;
      
      // On web, we can simulate haptics with vibration API
      if (!this.isIOS && 'vibrate' in navigator) {
        this.isAvailable = true;
      }
    } catch (_error) {
      // console.warn('Haptic engine initialization failed:', error);
      this.isAvailable = false;
    }
  }

  /**
   * Selection feedback - used for tab switching
   * Provides subtle feedback when user makes a selection
   */
  async selection(): Promise<void> {
    if (!this.isAvailable) return;

    try {
      if (this.isIOS && window.DeviceMotionEvent && 'requestPermission' in window.DeviceMotionEvent) {
        // Use iOS native haptics if available
        if (navigator.vibrate) {
          navigator.vibrate(10); // Very brief vibration
        }
      } else if (navigator.vibrate) {
        // Fallback for web/Android
        navigator.vibrate(10);
      }
    } catch (_error) {
      // console.warn('Selection haptic feedback failed:', error);
    }
  }

  /**
   * Impact feedback - used for button presses and interactions
   */
  async impact(intensity: 'light' | 'medium' | 'heavy' = 'medium'): Promise<void> {
    if (!this.isAvailable) return;

    try {
      const intensityMap = {
        light: 20,
        medium: 40,
        heavy: 80
      };

      const duration = intensityMap[intensity];
      
      if (navigator.vibrate) {
        navigator.vibrate(duration);
      }
    } catch (_error) {
      // console.warn('Impact haptic feedback failed:', error);
    }
  }

  /**
   * Notification feedback - used for alerts, confirmations, errors
   */
  async notification(type: 'success' | 'warning' | 'error'): Promise<void> {
    if (!this.isAvailable) return;

    try {
      const patterns = {
        success: [40, 60, 40], // Short-pause-short pattern
        warning: [60, 40, 60, 40, 60], // Three pulses
        error: [100, 50, 100] // Two strong pulses
      };

      const pattern = patterns[type];
      
      if (navigator.vibrate) {
        navigator.vibrate(pattern);
      }
    } catch (_error) {
      // console.warn('Notification haptic feedback failed:', error);
    }
  }

  /**
   * Custom haptic pattern
   */
  async custom(pattern: number | number[]): Promise<void> {
    if (!this.isAvailable) return;

    try {
      if (navigator.vibrate) {
        navigator.vibrate(pattern);
      }
    } catch (_error) {
      // console.warn('Custom haptic feedback failed:', error);
    }
  }

  /**
   * Check if haptic feedback is available
   */
  isHapticAvailable(): boolean {
    return this.isAvailable;
  }

  /**
   * Get platform information
   */
  getPlatformInfo(): { isIOS: boolean; isAvailable: boolean } {
    return {
      isIOS: this.isIOS,
      isAvailable: this.isAvailable
    };
  }
}

// Singleton instance
const hapticEngine = new HapticEngineManager();

// Export the haptic engine functions
export const HapticEngine = {
  /**
   * Selection feedback for UI interactions like tab switching
   */
  selection: () => hapticEngine.selection(),

  /**
   * Impact feedback for button presses and touch interactions
   */
  impact: (intensity?: 'light' | 'medium' | 'heavy') => hapticEngine.impact(intensity),

  /**
   * Notification feedback for system alerts and confirmations
   */
  notification: (type: 'success' | 'warning' | 'error') => hapticEngine.notification(type),

  /**
   * Custom haptic patterns
   */
  custom: (pattern: number | number[]) => hapticEngine.custom(pattern),

  /**
   * Check availability of haptic feedback
   */
  isAvailable: () => hapticEngine.isHapticAvailable(),

  /**
   * Get platform information
   */
  getPlatformInfo: () => hapticEngine.getPlatformInfo()
};

// Convenience functions for common haptic patterns
export const HapticPatterns = {
  /**
   * Quick tap feedback
   */
  quickTap: () => HapticEngine.impact('light'),

  /**
   * Button press feedback
   */
  buttonPress: () => HapticEngine.impact('medium'),

  /**
   * Heavy interaction feedback
   */
  heavyPress: () => HapticEngine.impact('heavy'),

  /**
   * Tab selection feedback
   */
  tabSelection: () => HapticEngine.selection(),

  /**
   * Success confirmation
   */
  success: () => HapticEngine.notification('success'),

  /**
   * Warning alert
   */
  warning: () => HapticEngine.notification('warning'),

  /**
   * Error alert
   */
  error: () => HapticEngine.notification('error'),

  /**
   * Scroll bounce effect (iOS-like)
   */
  scrollBounce: () => HapticEngine.custom([10, 20, 10]),

  /**
   * Pull-to-refresh pattern
   */
  pullToRefresh: () => HapticEngine.custom([30, 100, 30])
};

export default HapticEngine;